import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import api from '../../services/api';
import { BORDER_CITIES_BY_STATE } from '../../constants/constants';
import { revalidatePublicPath } from '../../utils/revalidatePublic';

const EMPTY_SEE_MORE = {
  es: { label: 'VER MÁS >', title: '', href: '/es#home-browse' },
  en: { label: 'SEE MORE >', title: '', href: '/en#home-browse' },
};

function newItemId() {
  return `hcb-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function emptyItem() {
  return {
    id: newItemId(),
    order: 0,
    label: '',
    use_entry_departure_city_name: true,
    link_title: '',
    entry_page_id: '',
    entry_page_slug: '',
  };
}

function usesDepartureCityName(item) {
  return !(
    item?.use_entry_departure_city_name === false ||
    item?.use_entry_departure_city_name === 'false'
  );
}

function departureCityName(page) {
  const cityCode = page?.departure?.city || page?.city || '';
  const stateCode = page?.departure?.state || page?.state || '';
  if (cityCode && stateCode && BORDER_CITIES_BY_STATE[stateCode]) {
    return (
      BORDER_CITIES_BY_STATE[stateCode].find((c) => c.code === cityCode)?.name ||
      cityCode
    );
  }
  return cityCode || '';
}

function itemDisplayLabel(item, entryPages) {
  if (!usesDepartureCityName(item)) {
    return String(item?.label || '').trim();
  }
  const page = (entryPages || []).find(
    (p) => String(p._id) === String(item?.entry_page_id)
  );
  return departureCityName(page) || String(item?.label || '').trim();
}

function pageMatchesLang(page, langKey) {
  const langUpper = String(langKey || '').toUpperCase();
  const pageLang = String(page?.lang || '').toUpperCase();
  if (pageLang && pageLang === langUpper) return true;

  const slug = String(page?.full_slug || page?.slug || '').toLowerCase();
  const prefix = `/${String(langKey || '').toLowerCase()}/`;
  if (slug.startsWith(prefix) || slug === `/${String(langKey || '').toLowerCase()}`) {
    return true;
  }
  return false;
}

function toPageOption(page) {
  const id = page?._id != null ? String(page._id) : '';
  const slug = page?.full_slug || page?.slug || id;
  const city = departureCityName(page);
  return {
    label: city ? `${slug} · ${city}` : slug,
    value: id,
    page,
  };
}

function toSaveItem(item, index) {
  return {
    id: item.id,
    order: index,
    label: String(item.label || '').trim(),
    use_entry_departure_city_name: usesDepartureCityName(item),
    link_title: String(item.link_title || '').trim(),
    entry_page_id: item.entry_page_id,
    entry_page_slug: item.entry_page_slug || '',
  };
}

function normalizeLoadedItems(raw) {
  const mapList = (list) =>
    (Array.isArray(list) ? list : []).map((item, index) => ({
      ...emptyItem(),
      ...item,
      id: item.id || newItemId(),
      order: item.order ?? index,
      entry_page_id: item.entry_page_id ? String(item.entry_page_id) : '',
      use_entry_departure_city_name: usesDepartureCityName(item),
    }));

  if (Array.isArray(raw)) {
    return { es: mapList(raw), en: [] };
  }
  return {
    es: mapList(raw?.es),
    en: mapList(raw?.en),
  };
}

function ActionBar({ onSave, onPublish, saving, publishing }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid #e5e7eb',
      }}
    >
      <Button
        type="button"
        label="Publish"
        icon="pi pi-send"
        className="p-button-warning"
        loading={publishing}
        disabled={saving}
        onClick={onPublish}
        style={{ width: 180 }}
      />
      <Button
        type="button"
        label="Save"
        icon="pi pi-save"
        className="main-button"
        loading={saving}
        disabled={publishing}
        onClick={onSave}
        style={{ width: 180 }}
      />
    </div>
  );
}

function LangCitiesSection({
  langKey,
  items,
  seeMore,
  entryPages,
  onAdd,
  onRemove,
  onMove,
  onChangeItem,
  onEntryPageChange,
  onSeeMoreChange,
}) {
  const langPath = `/${langKey}`;
  const lastCardRef = useRef(null);
  const prevCountRef = useRef(items.length);

  const pageOptions = useMemo(
    () =>
      (entryPages || [])
        .filter((p) => pageMatchesLang(p, langKey))
        .map(toPageOption)
        .filter((opt) => opt.value),
    [entryPages, langKey]
  );

  useEffect(() => {
    if (items.length > prevCountRef.current) {
      lastCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    prevCountRef.current = items.length;
  }, [items.length]);

  return (
    <div>
      <Message
        severity="info"
        className="mb-3 w-full justify-content-start"
        text={`Cities for ${langPath}. Visible text uses the entry page departure city name unless you add custom text. Each item links to an entry page in this language. "See more" uses a same-domain path/slug.`}
      />

      <Panel header={`See more (${langPath})`} className="mb-4" toggleable>
        <div className="grid">
          <div className="col-12 md:col-4">
            <label>
              <strong>Visible label</strong>
            </label>
            <InputText
              className="w-full"
              value={seeMore?.label || ''}
              placeholder={''}
              onChange={(e) => onSeeMoreChange({ label: e.target.value })}
            />
          </div>
          <div className="col-12 md:col-4">
            <label>
              <strong>Link title</strong>
            </label>
            <InputText
              className="w-full"
              value={seeMore?.title || ''}
              placeholder="title attribute"
              onChange={(e) => onSeeMoreChange({ title: e.target.value })}
            />
          </div>
          <div className="col-12 md:col-4">
            <label>
              <strong>Href (slug / path)</strong>
            </label>
            <InputText
              className="w-full"
              value={seeMore?.href || ''}
              placeholder={`/${langKey}#home-browse`}
              onChange={(e) => onSeeMoreChange({ href: e.target.value })}
            />
            <small className="text-600">Same domain only — path like /{langKey}/mexico</small>
          </div>
        </div>
      </Panel>

      <div className="flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <small className="text-600">
          {pageOptions.length
            ? `${pageOptions.length} entry page${pageOptions.length === 1 ? '' : 's'} for ${langPath}`
            : `No entry pages matched for ${langPath}`}
        </small>
        <Button type="button" label="Add city" icon="pi pi-plus" onClick={onAdd} />
      </div>

      {items.length === 0 && (
        <Message
          severity="warn"
          className="mb-3 w-full justify-content-start"
          text={`No cities for ${langPath} yet.`}
        />
      )}

      {items.map((item, index) => {
        const displayLabel = itemDisplayLabel(item, entryPages);
        const selectedPage = entryPages.find(
          (p) => String(p._id) === String(item.entry_page_id)
        );
        return (
        <div
          key={item.id}
          ref={index === items.length - 1 ? lastCardRef : null}
          style={{ scrollMarginTop: 16 }}
        >
        <Panel
          header={`City ${index + 1}${displayLabel ? ` — ${displayLabel}` : ''}`}
          className="mb-3"
          toggleable
        >
          <div className="flex justify-content-end gap-1 mb-3">
            <Button
              type="button"
              icon="pi pi-arrow-up"
              className="p-button-text p-button-sm"
              disabled={index === 0}
              onClick={() => onMove(index, -1)}
            />
            <Button
              type="button"
              icon="pi pi-arrow-down"
              className="p-button-text p-button-sm"
              disabled={index === items.length - 1}
              onClick={() => onMove(index, 1)}
            />
            <Button
              type="button"
              icon="pi pi-trash"
              className="p-button-danger p-button-sm"
              onClick={() => onRemove(index)}
            />
          </div>

          <div className="grid">
            <div className="col-12 md:col-6">
              <label>
                <strong>Entry page</strong>
              </label>
              <Dropdown
                value={item.entry_page_id ? String(item.entry_page_id) : null}
                options={pageOptions}
                optionLabel="label"
                optionValue="value"
                placeholder={
                  pageOptions.length
                    ? 'Select entry page'
                    : `No ${langKey.toUpperCase()} entry pages`
                }
                className="w-full"
                filter
                filterBy="label"
                filterInputAutoFocus
                showClear
                onChange={(e) => onEntryPageChange(index, e.value)}
              />
              {item.entry_page_slug ? (
                <small className="text-600">{item.entry_page_slug}</small>
              ) : null}
            </div>
            <div className="col-12 md:col-6 flex align-items-end">
              <div className="flex align-items-center gap-2 mb-2">
                <Checkbox
                  inputId={`use-dep-city-${item.id}`}
                  checked={usesDepartureCityName(item)}
                  onChange={(e) => {
                    const checked = Boolean(e.checked);
                    const patch = { use_entry_departure_city_name: checked };
                    if (checked) {
                      const city = departureCityName(selectedPage);
                      if (city) patch.label = city;
                    }
                    onChangeItem(index, patch);
                  }}
                />
                <label htmlFor={`use-dep-city-${item.id}`} className="m-0">
                  Use entry departure city name
                </label>
              </div>
            </div>
            {usesDepartureCityName(item) ? (
              <div className="col-12">
                <small className="text-600">
                  Bar will show the selected entry page&apos;s departure city name
                  {displayLabel ? ` (${displayLabel})` : ''}.
                </small>
              </div>
            ) : (
              <div className="col-12 md:col-6">
                <label>
                  <strong>Custom visible text</strong>
                </label>
                <InputText
                  className="w-full"
                  value={item.label || ''}
                  placeholder="City name or custom text"
                  onChange={(e) => onChangeItem(index, { label: e.target.value })}
                />
              </div>
            )}
          </div>
        </Panel>
        </div>
        );
      })}
    </div>
  );
}

export default function HomeCityBarManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [entryPages, setEntryPages] = useState([]);
  const [itemsByLang, setItemsByLang] = useState({ es: [], en: [] });
  const [seeMore, setSeeMore] = useState(EMPTY_SEE_MORE);
  const [activeIndex, setActiveIndex] = useState(0);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, pagesRes] = await Promise.all([
        api.get('/home_city_bar'),
        api.get('/entry_pages/summary').catch(() => null),
      ]);

      let pages = Array.isArray(pagesRes?.data) ? pagesRes.data : [];
      const missingLang = pages.length === 0 || pages.every((p) => !p.lang);
      if (missingLang) {
        try {
          const langRes = await api.get('/entry_pages/translations');
          const withLang = Array.isArray(langRes.data) ? langRes.data : [];
          if (pages.length === 0) {
            pages = withLang;
          } else {
            const langById = new Map(withLang.map((p) => [String(p._id), p.lang]));
            pages = pages.map((p) => ({
              ...p,
              lang: p.lang || langById.get(String(p._id)) || '',
            }));
          }
        } catch (e) {
          console.warn('Could not enrich entry pages with lang', e);
        }
      }

      const data = configRes.data || {};
      setItemsByLang(normalizeLoadedItems(data.items));
      setSeeMore({
        es: { ...EMPTY_SEE_MORE.es, ...(data.seeMore?.es || {}) },
        en: { ...EMPTY_SEE_MORE.en, ...(data.seeMore?.en || {}) },
      });
      setEntryPages(pages);
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load home city bar config',
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateItem = (langKey, index, patch) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [langKey]: list };
    });
  };

  const handleEntryPageChange = (langKey, index, pageId) => {
    const page = entryPages.find((p) => String(p._id) === String(pageId));
    const cityHint = departureCityName(page);
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      const current = list[index] || emptyItem();
      const useCity = usesDepartureCityName(current);
      list[index] = {
        ...current,
        entry_page_id: pageId || '',
        entry_page_slug: page?.full_slug || '',
        label: useCity ? cityHint || current.label || '' : current.label || cityHint || '',
      };
      return { ...prev, [langKey]: list };
    });
  };

  const addItem = (langKey) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      list.push({ ...emptyItem(), order: list.length });
      return { ...prev, [langKey]: list };
    });
  };

  const removeItem = (langKey, index) => {
    setItemsByLang((prev) => ({
      ...prev,
      [langKey]: (prev[langKey] || [])
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, order: i })),
    }));
  };

  const moveItem = (langKey, index, dir) => {
    setItemsByLang((prev) => {
      const list = [...(prev[langKey] || [])];
      const target = index + dir;
      if (target < 0 || target >= list.length) return prev;
      const tmp = list[index];
      list[index] = list[target];
      list[target] = tmp;
      return {
        ...prev,
        [langKey]: list.map((item, i) => ({ ...item, order: i })),
      };
    });
  };

  const onSave = async ({ silent = false } = {}) => {
    const all = [...(itemsByLang.es || []), ...(itemsByLang.en || [])];
    const invalid = all.some((item) => {
      if (!item.entry_page_id) return true;
      if (!usesDepartureCityName(item) && !String(item.label || '').trim()) return true;
      return false;
    });
    if (invalid) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Incomplete',
        detail:
          'Each city needs an entry page, and custom text if Use entry departure city name is off.',
        life: 4000,
      });
      return false;
    }

    setSaving(true);
    try {
      await api.put('/home_city_bar', {
        items: {
          es: (itemsByLang.es || []).map((item, index) => toSaveItem(item, index)),
          en: (itemsByLang.en || []).map((item, index) => toSaveItem(item, index)),
        },
        seeMore,
      });

      if (!silent) {
        toast.current?.show({
          severity: 'success',
          summary: 'Saved',
          detail: 'Home city bar saved. Use Publish to refresh /es and /en.',
          life: 3500,
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'Save failed',
        life: 4000,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    const saved = await onSave({ silent: true });
    if (!saved) return;

    const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';
    if (!publicUrl || !secret) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Saved, not published',
        detail: 'Missing REACT_APP_BORDERGARITA_PUBLIC_APP_URL or REVALIDATE_SECRET',
        life: 5000,
      });
      return;
    }

    setPublishing(true);
    try {
      for (const path of ['/es', '/en']) {
        await revalidatePublicPath(publicUrl, secret, path);
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Published',
        detail: 'Revalidated /es and /en',
        life: 3500,
      });
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: 'error',
        summary: 'Publish failed',
        detail: err?.message || 'Revalidate error',
        life: 5000,
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="p-3">
      <Toast ref={toast} />
      <Card title="Home city bar" subTitle="Mint bar under the logo on /es and /en">
        {loading ? (
          <p>Loading…</p>
        ) : (
          <>
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
              <TabPanel header={`/es (${itemsByLang.es.length})`}>
                <LangCitiesSection
                  langKey="es"
                  items={itemsByLang.es || []}
                  seeMore={seeMore.es}
                  entryPages={entryPages}
                  onAdd={() => addItem('es')}
                  onRemove={(i) => removeItem('es', i)}
                  onMove={(i, d) => moveItem('es', i, d)}
                  onChangeItem={(i, patch) => updateItem('es', i, patch)}
                  onEntryPageChange={(i, id) => handleEntryPageChange('es', i, id)}
                  onSeeMoreChange={(patch) =>
                    setSeeMore((prev) => ({ ...prev, es: { ...prev.es, ...patch } }))
                  }
                />
              </TabPanel>
              <TabPanel header={`/en (${itemsByLang.en.length})`}>
                <LangCitiesSection
                  langKey="en"
                  items={itemsByLang.en || []}
                  seeMore={seeMore.en}
                  entryPages={entryPages}
                  onAdd={() => addItem('en')}
                  onRemove={(i) => removeItem('en', i)}
                  onMove={(i, d) => moveItem('en', i, d)}
                  onChangeItem={(i, patch) => updateItem('en', i, patch)}
                  onEntryPageChange={(i, id) => handleEntryPageChange('en', i, id)}
                  onSeeMoreChange={(patch) =>
                    setSeeMore((prev) => ({ ...prev, en: { ...prev.en, ...patch } }))
                  }
                />
              </TabPanel>
            </TabView>

            <ActionBar
              onSave={() => onSave()}
              onPublish={onPublish}
              saving={saving}
              publishing={publishing}
            />
          </>
        )}
      </Card>
    </div>
  );
}
